import { useUser } from '@/contexts/UserContext';
import type {FeatureConfig, CourseID, FeatureConfigKey} from '@/models';

interface UseFeatureAccessReturn {
  hasFeature: (feature: FeatureConfigKey) => boolean;
  hasCourseAccess: (courseId: CourseID) => boolean;
  hasAllCourses: () => boolean;
  hasActiveSubscription: () => boolean;
  featureConfig: FeatureConfig | null;
}

/**
 * Hook to check user's access to specific features based on FeatureConfig
 */
export function useFeatureAccess(): UseFeatureAccessReturn {
  const { user } = useUser();

  const hasFeature = (feature: FeatureConfigKey): boolean => {
    if (user === null) return false;

    const value = user.featureConfig[feature];

    // Handle boolean features
    if (typeof value === 'boolean') {
      return value;
    }

    // For other types (like courses object), return false
    return false;
  };

  const hasCourseAccess = (courseId: CourseID): boolean => {
    if (user === null) return false;

    // Check if user has access to all courses
    if (user.featureConfig.allCourses) {
      return true;
    }

    // Check if user has access to specific course
    return user.featureConfig.courses[courseId];
  };

  const hasAllCourses = (): boolean => {
    if (user === null) return false;
    return user.featureConfig.allCourses;
  };

  // Whether the user currently has any active subscription. Free courses are only
  // accessible to users WITHOUT an active subscription.
  const hasActiveSubscription = (): boolean => {
    if (user === null) return false;
    return user.activeSubscriptionTypes.length > 0;
  };

  return {
    hasFeature,
    hasCourseAccess,
    hasAllCourses,
    hasActiveSubscription,
    featureConfig: user?.featureConfig ?? null
  };
}
